import { c as _c } from "react/compiler-runtime";
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Checkbox, CheckboxTree, Search, useTranslationContext } from 'platform/components';
import { startTransition, useState } from 'react';
import { sort } from 'ramda';
import { useColumnHidingAllowed } from '../../../../store/selectors';
import { useColumnStates } from '../../../../store/selectors/useColumnStates';
import { useEagGridStore } from '../../../../store/useEagGridStore';
import type { EagColumn } from '../../../../types/EagColumn';
import type { EagColumnOrGroup } from '../../../../types/EagColumnGroup';
import type { EagColumnState } from '../../../../types/EagColumnState';
import { isEagColumnGroup } from '../../../../utils/isEagColumnGroup';
import { SortableColumnItem } from './components/SortableColumnItem/SortableColumnItem';
import { buildCheckboxTreeOptions } from './utils/buildCheckboxTreeOptions';
import { computeToggleAllState } from './utils/computeToggleAllState';
import { filterCheckboxTreeOptions, filterFlatColumns, getMatchingColumnKeys } from './utils/filterColumnsBySearch';
export type ColumnsPanelProps = {
  columns: EagColumn[];
  columnConfig?: EagColumnOrGroup[];
};
const DRAG_ACTIVATION_DISTANCE = 8;
export function ColumnsPanel(props) {
  const $ = _c(62);
  const t = useTranslationContext();
  const store = useEagGridStore();
  const columnStates = useColumnStates();
  const columnHidingAllowed = useColumnHidingAllowed();
  const [searchTerm, setSearchTerm] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      activationConstraint: {
        distance: DRAG_ACTIVATION_DISTANCE
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const sensors = useSensors(useSensor(PointerSensor, t0));
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== columnHidingAllowed || $[2] !== columnStates || $[3] !== props.columnConfig || $[4] !== props.columns || $[5] !== searchTerm || $[6] !== sensors || $[7] !== store || $[8] !== t) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedColumnStates = sort(_temp, columnStates);
      const columnByKey = new Map(props.columns.map(_temp2));
      let t10;
      if ($[20] !== columnStates) {
        t10 = new Map(columnStates.map(_temp3));
        $[20] = columnStates;
        $[21] = t10;
      } else {
        t10 = $[21];
      }
      const columnStateByKey = t10;
      const sortedColumns = sortedColumnStates.map(state => columnByKey.get(state.key)).filter(_temp4);
      const hasGroupConfig = props.columnConfig && props.columnConfig.some(isEagColumnGroup);
      let visibleKeys;
      if ($[22] !== columnStates) {
        visibleKeys = [];
        for (const cs_0 of columnStates) {
          if (!(cs_0.hidden ?? false)) {
            visibleKeys.push(cs_0.key);
          }
        }
        $[22] = columnStates;
        $[23] = visibleKeys;
      } else {
        visibleKeys = $[23];
      }
      const lowerSearch = searchTerm.toLowerCase();
      const matchingKeys = getMatchingColumnKeys(props.columnConfig, props.columns, columnStates, lowerSearch);
      const toggleState = computeToggleAllState(columnStates, matchingKeys);
      let t11;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = value => {
          startTransition(() => {
            setSearchTerm(value ?? "");
          });
        };
        $[24] = t11;
      } else {
        t11 = $[24];
      }
      const handleSearchChange = t11;
      const handleToggleAll = () => {
        const shouldHide = toggleState.isAllEnabled;
        const keysToUpdate = new Set(toggleState.visibleColumnKeys);
        store.setColumnStates(columnStates.map(cs_1 => keysToUpdate.has(cs_1.key) ? {
          ...cs_1,
          hidden: shouldHide
        } : cs_1));
      };
      let t12;
      if ($[25] !== columnStates || $[26] !== store) {
        t12 = nextVisible => {
          const nextVisibleSet = new Set(nextVisible ?? []);
          store.setColumnStates(columnStates.map(cs_2 => {
            const shouldBeVisible = nextVisibleSet.has(cs_2.key);
            const currentlyVisible = !(cs_2.hidden ?? false);
            if (shouldBeVisible !== currentlyVisible) {
              return {
                ...cs_2,
                hidden: !shouldBeVisible
              };
            }
            return cs_2;
          }));
        };
        $[25] = columnStates;
        $[26] = store;
        $[27] = t12;
      } else {
        t12 = $[27];
      }
      const handleCheckboxTreeChange = t12;
      let t13;
      if ($[28] !== store) {
        t13 = event => {
          const activeId = event.active.id as string;
          const overId = event.over?.id as string | undefined;
          if (!overId || activeId === overId) {
            return;
          }
          store.reorderColumnsByKey(activeId, overId);
        };
        $[28] = store;
        $[29] = t13;
      } else {
        t13 = $[29];
      }
      const handleDragEnd = t13;
      const t14 = searchTerm || null;
      let t15;
      if ($[30] !== t14) {
        t15 = <div className="px-4 pt-3 pb-2 flex-shrink-0"><Search value={t14} onChange={handleSearchChange} data-testid="columns-panel-search" /></div>;
        $[30] = t14;
        $[31] = t15;
      } else {
        t15 = $[31];
      }
      let t16;
      if ($[32] !== t) {
        t16 = t("page.datagrid.labels.toggleAllColumns");
        $[32] = t;
        $[33] = t16;
      } else {
        t16 = $[33];
      }
      const t17 = <div className="px-4 pb-2 flex-shrink-0"><Checkbox label={t16} value={toggleState.isAllEnabled} isIndeterminate={toggleState.isIndeterminate} onChange={handleToggleAll} isDisabled={!columnHidingAllowed} data-testid="columns-panel-toggle-all" /></div>;
      let t18;
      if ($[34] !== t15 || $[35] !== t17) {
        t18 = <div className="flex flex-col gap-3">{t15}{t17}</div>;
        $[34] = t15;
        $[35] = t17;
        $[36] = t18;
      } else {
        t18 = $[36];
      }
      const panelHeader = t18;
      if (hasGroupConfig) {
        const filteredOptions = filterCheckboxTreeOptions(buildCheckboxTreeOptions(props.columnConfig ?? []), lowerSearch);
        const t19 = <div className="flex-1 overflow-y-auto py-2 px-4"><CheckboxTree options={filteredOptions} value={visibleKeys} onChange={columnHidingAllowed ? handleCheckboxTreeChange : undefined} isDisabled={!columnHidingAllowed} /></div>;
        let t20;
        if ($[37] !== panelHeader || $[38] !== t19) {
          t20 = <div className="flex flex-col h-full overflow-hidden">{panelHeader}{t19}</div>;
          $[37] = panelHeader;
          $[38] = t19;
          $[39] = t20;
        } else {
          t20 = $[39];
        }
        t9 = t20;
        break bb0;
      }
      const filteredColumns = filterFlatColumns(sortedColumns, lowerSearch);
      const filteredIds = filteredColumns.map(_temp5);
      const isDragDisabled = searchTerm.length > 0;
      t7 = "flex flex-col h-full overflow-hidden";
      t8 = panelHeader;
      t6 = "flex-1 overflow-y-auto py-2";
      T1 = DndContext;
      t4 = sensors;
      t5 = handleDragEnd;
      T0 = SortableContext;
      t1 = filteredIds;
      t2 = verticalListSortingStrategy;
      let t19;
      if ($[40] !== columnHidingAllowed || $[41] !== columnStateByKey || $[42] !== isDragDisabled || $[43] !== store.setColumnHidden) {
        t19 = column => {
          const columnState = columnStateByKey.get(column.key);
          if (!columnState) {
            return null;
          }
          return <SortableColumnItem key={column.key} column={column} columnState={columnState} onSetHidden={store.setColumnHidden} columnHidingAllowed={columnHidingAllowed} isDragDisabled={isDragDisabled} />;
        };
        $[40] = columnHidingAllowed;
        $[41] = columnStateByKey;
        $[42] = isDragDisabled;
        $[43] = store.setColumnHidden;
        $[44] = t19;
      } else {
        t19 = $[44];
      }
      t3 = filteredColumns.map(t19);
    }
    $[1] = columnHidingAllowed;
    $[2] = columnStates;
    $[3] = props.columnConfig;
    $[4] = props.columns;
    $[5] = searchTerm;
    $[6] = sensors;
    $[7] = store;
    $[8] = t;
    $[9] = T0;
    $[10] = T1;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[45] !== T0 || $[46] !== t1 || $[47] !== t2 || $[48] !== t3) {
    t10 = <T0 items={t1} strategy={t2}>{t3}</T0>;
    $[45] = T0;
    $[46] = t1;
    $[47] = t2;
    $[48] = t3;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== T1 || $[51] !== t10 || $[52] !== t4 || $[53] !== t5) {
    t11 = <T1 sensors={t4} onDragEnd={t5}>{t10}</T1>;
    $[50] = T1;
    $[51] = t10;
    $[52] = t4;
    $[53] = t5;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  let t12;
  if ($[55] !== t11 || $[56] !== t6) {
    t12 = <div className={t6}>{t11}</div>;
    $[55] = t11;
    $[56] = t6;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  let t13;
  if ($[58] !== t12 || $[59] !== t7 || $[60] !== t8) {
    t13 = <div className={t7}>{t8}{t12}</div>;
    $[58] = t12;
    $[59] = t7;
    $[60] = t8;
    $[61] = t13;
  } else {
    t13 = $[61];
  }
  return t13;
}
function _temp5(col_1) {
  return col_1.key;
}
function _temp4(col_0) {
  return col_0 !== undefined;
}
function _temp3(cs) {
  return [cs.key, cs];
}
function _temp2(col) {
  return [col.key, col];
}
function _temp(a, b) {
  return a.order - b.order;
}